import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import RatingStars from '@repo/ui/src/components/RatingStars';
import InlineRecipeCardInfo from '@components/InlineRecipe/_InlineRecipeCardInfo';
import InlineIngredientSummary from '@components/InlineRecipe/_InlineIngredientSummary';
import PrintButton from '@views/RecipeDetailPage/_PrintButton';
import { useCreateRecipeTime } from '@hooks/recipe/useCreateRecipeTime';
import { UnitOfMeasureEnum } from '@enums/recipe-enums';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import KnifeAndFork from '@repo/ui/src/components/Icons/KnifeAndFork';
import {
  AuthorProps,
  IngredientSummaryProps,
  MetaProps,
  RecipeMetaSectionProps,
  RecipeStepProps,
} from '@repo/ui/src/types';

export interface InlineRecipeProps {
  id: string;
  recipeMetaSection: RecipeMetaSectionProps;
  author: AuthorProps;
  ratingSummary: {
    average: number;
    count: number;
  };
  ingredientSummary?: IngredientSummaryProps;
  steps: Array<RecipeStepProps>;
  _meta: MetaProps;
}
const InlineRecipe = ({
  recipeMetaSection,
  author,
  ratingSummary,
  ingredientSummary,
  steps,
  _meta,
  id,
}: InlineRecipeProps) => {
  const { t } = useTranslation();
  const cookTime = useCreateRecipeTime(recipeMetaSection?.cookTime);
  const prepTime = useCreateRecipeTime(recipeMetaSection?.prepTime);

  const segmentRecipe: any = {
    recipe_id: id,
    recipe_name: recipeMetaSection?.title,
    recipe_url: `https://traeger.com/${_meta?.deliveryKey}`,
    recipe_author_name: author.name,
    pellet_recommendation: recipeMetaSection?.hardwood,
    recipe_serving_size: recipeMetaSection.servingSizeMin,
    recipe_measurement_units: 'Imperial',
    recipe_prep_time: recipeMetaSection?.prepTime,
    recipe_cook_time: recipeMetaSection?.cookTime,
  };

  return (
    <section className="tw-mt-12 md:tw-mt-20">
      <div
        className="tw-bg-surface-100 tw-px-5 tw-py-10 tw-text-center tw-border-2 tw-border-b-0 tw-border-surface-300 md:tw-pb-12"
        id="inline-recipe-container"
      >
        <h5 className="tra-text-web-heading-5">{recipeMetaSection.title}</h5>
        <p className="tra-text-web-body-s tw-py-5">by {author.name}</p>
        {ratingSummary?.average && (
          <RatingStars
            value={ratingSummary?.average}
            reviewCount={ratingSummary.count}
            className="tw-justify-center"
          />
        )}
        <InlineRecipeCardInfo {...recipeMetaSection} prepTime={prepTime} cookTime={cookTime} />
      </div>
      <div className="tw-px-4 md:tw-px-10 tw-pt-10 tw-pb-20 tw-border-2 tw-border-surface-300">
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-center tw-gap-x-10 tw-gap-y-4 tw-pb-10">
          <PrintButton
            servingSize={recipeMetaSection?.servingSizeMin}
            authorName={author.name}
            ingredientSummary={ingredientSummary}
            unitOfMeasure={UnitOfMeasureEnum.Imperial}
            recipeMetaSection={recipeMetaSection}
            prepTime={prepTime}
            cookTime={cookTime}
            steps={steps}
            segmentRecipe={segmentRecipe}
          />
          <Button
            as={Anchor}
            variant={ButtonVariant.secondary}
            link={_meta?.deliveryKey}
            className="tw-inline-flex tw-justify-center tw-items-center tw-w-full md:tw-w-auto"
          >
            <KnifeAndFork className="tw-h-6 tw-w-6 tw-mr-2" />
            {t('recipeContent.viewRecipe', undefined, { default: 'View Recipe' })}
          </Button>
        </div>
        <p className="tra-text-web-body-l">{recipeMetaSection?.description}</p>
        <h6 className="tra-text-web-heading-6 tw-pt-6 tw-pb-[28px]">
          {t('recipeContent.ingredientsLabel')}
        </h6>
        {ingredientSummary?.groups?.map((group, i) => (
          <InlineIngredientSummary {...group} key={i} />
        ))}
        <ul className="tw-list-none tw-pt-20 tw-pl-0">
          <h6 className="tra-text-web-heading-6 tw-pb-6">{t('recipeContent.stepsLabel')}</h6>
          {steps?.map((step, i) => (
            <li
              key={i}
              data-test-id={`recipe_step_${i + 1}`}
              className="tw-list-none tw-pb-6 last:tw-pb-0"
            >
              <div className="tw-flex">
                <p className="tra-text-web-heading-7 tw-pr-6">{i + 1}</p>
                <p className="tra-text-web-body-l">{step?.instruction}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default InlineRecipe;
